import type {
  BalanceActivityPublic,
  BalanceEntryPublic,
  BalanceInterestPublic,
  BalanceListItemPublic,
  BalancePaymentCreate,
  BalanceSummaryPublic,
  MemberBalancePublic,
} from '../type/balance';
import type { CashPaymentType } from '../type/payment';
import { toThreeDigits } from '@/lib/utils';
import type {
  BalanceActivity,
  BalanceEntry,
  BalanceInterestRow,
  BalanceListItem,
  BalanceOverview,
  MemberBalance,
} from '@/types';

const BalanceActivityMapper = (activity?: BalanceActivityPublic | null): BalanceActivity | null =>
  activity
    ? {
        date: activity.date,
        kind: activity.kind,
        source: activity.source,
        refNo: activity.ref_no || undefined,
        amount: activity.amount,
        paymentType: activity.payment_type || undefined,
      }
    : null;

export const BalanceListItemMapper = (item: BalanceListItemPublic): BalanceListItem => ({
  memberId: String(item.user_id),
  memberNo: item.member_id != null ? toThreeDigits(item.member_id) : undefined,
  memberName: item.user_name,
  memberPhone: item.user_phone ?? '',
  cardNo: item.card_no || undefined,
  added: item.added,
  interestCharged: item.interest_charged,
  repaid: item.repaid,
  principalDue: item.principal_due,
  interestDue: item.interest_due,
  balanceDue: item.balance_due,
  since: item.since,
  status: item.status,
  lastActivity: BalanceActivityMapper(item.last_activity),
});

export const BalanceOverviewMapper = (summary?: BalanceSummaryPublic | null): BalanceOverview => ({
  balanceDue: summary?.balance_due ?? 0,
  membersWithBalance: summary?.members_with_balance ?? 0,
  interestDue: summary?.interest_due ?? 0,
  paidByPms: summary?.paid_by_pms ?? 0,
  paidByPmsPolicy: summary?.paid_by_pms_policy ?? 0,
  paidByPmsLoan: summary?.paid_by_pms_loan ?? 0,
  interestCharged: summary?.interest_charged ?? 0,
  interestThisMonth: summary?.interest_this_month ?? 0,
  repaid: summary?.repaid ?? 0,
  clearedMembers: summary?.cleared_members ?? 0,
  repayments: summary?.repayments ?? 0,
});

const BalanceEntryMapper = (entry: BalanceEntryPublic): BalanceEntry => ({
  id: String(entry.id),
  date: entry.date,
  month: entry.month,
  kind: entry.kind,
  source: entry.source,
  refNo: entry.ref_no || undefined,
  paymentType: entry.payment_type || undefined,
  amount: entry.amount,
  toInterest: entry.to_interest,
  toPrincipal: entry.to_principal,
  balanceAfter: entry.balance_after,
  note: entry.note || undefined,
});

const BalanceInterestMapper = (row: BalanceInterestPublic): BalanceInterestRow => ({
  month: row.month,
  balance: row.balance,
  interest: row.interest,
  totalInterest: row.total_interest,
  balanceDue: row.balance_due,
});

export const MemberBalanceMapper = (balance: MemberBalancePublic): MemberBalance => ({
  memberId: String(balance.user_id),
  memberNo: balance.member_id != null ? toThreeDigits(balance.member_id) : undefined,
  memberName: balance.user_name,
  cardNo: balance.card_no || undefined,
  added: balance.added,
  interestCharged: balance.interest_charged,
  repaid: balance.repaid,
  principalDue: balance.principal_due,
  interestDue: balance.interest_due,
  balanceDue: balance.balance_due,
  status: balance.status,
  entries: (balance.entries ?? []).map(BalanceEntryMapper),
  interest: (balance.interest ?? []).map(BalanceInterestMapper),
});

export const BalancePaymentRequestMapper = (input: { amount: number; paymentType: CashPaymentType }): BalancePaymentCreate => ({
  amount: input.amount,
  payment_type: input.paymentType,
});
